import React, { createContext, useContext, useEffect, useMemo } from "react";
import AuthContext from "./AuthContext";
import TenantContext from "./TenantContext";

/** Convert hex color (#RRGGBB) to space-separated RGB string for Tailwind CSS vars */
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

/** Generate a full shade palette (50–950) from a single hex color.
 *  The input color maps to the 600 shade; lighter/darker shades are
 *  computed by mixing toward white or black. */
function generatePalette(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (ratio, target) => {
    const t = target === "white" ? 255 : 0;
    return `${Math.round(r + (t - r) * ratio)} ${Math.round(g + (t - g) * ratio)} ${Math.round(b + (t - b) * ratio)}`;
  };
  return {
    50:  mix(0.95, "white"),
    100: mix(0.90, "white"),
    200: mix(0.80, "white"),
    300: mix(0.65, "white"),
    400: mix(0.45, "white"),
    500: mix(0.25, "white"),
    600: `${r} ${g} ${b}`,
    700: mix(0.15, "black"),
    800: mix(0.30, "black"),
    900: mix(0.45, "black"),
    950: mix(0.60, "black"),
  };
}

const OrgThemeContext = createContext({
  primaryColor: "#7C3AED",
  secondaryColor: "#4F46E5",
  recognitionColor: "#7C3AED",
  nominationColor: "#F97316",
  logoUrl: null,
  bannerImageUrl: null,
  bannerPosition: 50,
  welcomeTitle: null,
  welcomeMessage: null,
  appName: null,
  faviconUrl: null,
  avatarColor: null,
});

export const OrgThemeProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { scopedTheme } = useContext(TenantContext);

  const orgTheme = useMemo(() => {
    // Post-login: trust the user's organization_customization payload.
    // Pre-login on a tenant subdomain: fall back to the public theme so the
    // login screen shows org branding before any auth happens.
    const c = user?.organization_customization || scopedTheme || null;

    // Asset URLs stored by the backend are relative paths (/api/v1/org-assets/…).
    // Prefix with the API base so they resolve correctly when the React dev
    // server runs on a different port from the backend.
    const apiBase = process.env.REACT_APP_API_URL || "";
    const resolveUrl = (url) => (url ? (url.startsWith("/") ? `${apiBase}${url}` : url) : null);

    return {
      primaryColor: c?.primary_color || "#7C3AED",
      secondaryColor: c?.secondary_color || "#4F46E5",
      recognitionColor: c?.recognition_color || "#7C3AED",
      nominationColor: c?.nomination_color || "#F97316",
      logoUrl: c?.logo_url || null,
      bannerImageUrl: c?.background_image_url || null,
      bannerPosition: c?.banner_position ?? 50,
      welcomeTitle: c?.welcome_title || null,
      welcomeMessage: c?.welcome_message || null,
      appName: c?.app_name || null,
      faviconUrl: c?.favicon_url || null,
      avatarColor: c?.avatar_color || null,
    };
  }, [user?.organization_customization, scopedTheme]);

  // Sync org colors to CSS custom properties.
  // 1. --org-primary / --org-secondary: used by org-gradient CSS classes
  // 2. --org-purple-{shade}: overrides Tailwind's purple palette so ALL
  //    text-purple-*, bg-purple-*, border-purple-* etc. follow the org color
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--org-primary", orgTheme.primaryColor);
    root.style.setProperty("--org-secondary", orgTheme.secondaryColor);
    root.style.setProperty("--org-recognition", orgTheme.recognitionColor);
    root.style.setProperty("--org-nomination", orgTheme.nominationColor);

    const palette = generatePalette(orgTheme.primaryColor);
    Object.entries(palette).forEach(([shade, rgb]) => {
      root.style.setProperty(`--org-purple-${shade}`, rgb);
    });

    // Avatar color — read by getAvatarStyle() from the CSS property
    if (orgTheme.avatarColor) {
      root.style.setProperty("--org-avatar-color", orgTheme.avatarColor);
    } else {
      root.style.removeProperty("--org-avatar-color");
    }
  }, [orgTheme.primaryColor, orgTheme.secondaryColor, orgTheme.recognitionColor, orgTheme.nominationColor, orgTheme.avatarColor]);

  // Sync browser tab title + favicon from org customization
  useEffect(() => {
    if (orgTheme.appName) {
      document.title = orgTheme.appName;
    }
    if (orgTheme.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = orgTheme.faviconUrl;
    }
  }, [orgTheme.appName, orgTheme.faviconUrl]);

  return (
    <OrgThemeContext.Provider value={orgTheme}>
      {children}
    </OrgThemeContext.Provider>
  );
};

export default OrgThemeContext;
